import React, { useState, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface ImagesProp {
  id: number
  src: string
  alt: string
}

const ImageGallery = () => {
  const images: ImagesProp[] = []
  Array.from({ length: 56 }, (_, i) => i + 1).forEach((num) =>
    images.push({
      id: num,
      src: `/gallery/Picture ${num}.png`,
      alt: `Picture ${num}`,
    }),
  )

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  const thumbnailsRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (thumbnailsRef.current) {
      const thumbnail = thumbnailsRef.current.children[
        currentIndex
      ] as HTMLElement
      if (thumbnail) {
        const containerWidth = thumbnailsRef.current.clientWidth
        const scrollPosition =
          thumbnail.offsetLeft - containerWidth / 2 + thumbnail.clientWidth / 2
        thumbnailsRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        })
      }
    }
  }, [currentIndex])

  const nextSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    )
  }

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    )
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartX(e.pageX - (thumbnailsRef.current?.offsetLeft || 0))
    setScrollLeft(thumbnailsRef.current?.scrollLeft || 0)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    setStartX(e.touches[0].pageX - (thumbnailsRef.current?.offsetLeft || 0))
    setScrollLeft(thumbnailsRef.current?.scrollLeft || 0)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    if (thumbnailsRef.current) {
      const x = e.pageX - thumbnailsRef.current.offsetLeft
      const walk = (x - startX) * 2
      thumbnailsRef.current.scrollLeft = scrollLeft - walk
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return
    if (thumbnailsRef.current) {
      const x = e.touches[0].pageX - thumbnailsRef.current.offsetLeft
      const walk = (x - startX) * 2
      thumbnailsRef.current.scrollLeft = scrollLeft - walk
    }
  }

  const handleMouseUpOrLeave = () => {
    setIsDragging(false)
  }

  return (
    <div
      className="relative w-screen h-screen overflow-hidden"
      ref={containerRef}
    >
      {/* Main Image Display */}
      <div className="relative w-full h-full group">
        <img
          src={images[currentIndex].src}
          alt={images[currentIndex].alt}
          className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-105"
        />
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/60" />

        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-3 rounded-full transition-all duration-300 transform hover:scale-110 opacity-0 group-hover:opacity-100 hover:-translate-x-1 z-20"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/75 text-white p-3 rounded-full transition-all duration-300 transform hover:scale-110 opacity-0 group-hover:opacity-100 hover:translate-x-1 z-20"
        >
          <ChevronRight className="w-8 h-8" />
        </button>

        {/* Image Counter */}
        <div className="absolute top-4 right-4 bg-black/50 text-white px-4 py-2 rounded-full text-sm transform transition-all duration-300 opacity-0 group-hover:opacity-100 z-20">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Thumbnail Preview Section - Overlay with touch/mouse panning */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
          <div
            className="overflow-x-scroll scrollbar-none"
            ref={thumbnailsRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUpOrLeave}
            onMouseLeave={handleMouseUpOrLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleMouseUpOrLeave}
            style={{
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <div className="flex gap-2 px-4 min-w-max">
              {images.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => goToSlide(index)}
                  className={`flex-shrink-0 my-1 mx-1 relative transform transition-all duration-300 hover:scale-105 ${
                    currentIndex === index
                      ? 'ring-2 ring-white scale-105'
                      : 'hover:ring-2 hover:ring-white/50'
                  } rounded-md overflow-hidden`}
                >
                  <img
                    src={image.src}
                    alt={`Thumbnail ${index + 1}`}
                    className={`w-24 h-16 object-cover transition-opacity duration-300 ${
                      currentIndex !== index
                        ? 'opacity-50 hover:opacity-80'
                        : 'opacity-100'
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageGallery
